import type { FC, ImgHTMLAttributes } from "react";

// Import all flag SVGs as URLs using Vite's glob import
// The query: '?url' tells Vite to return the asset URL instead of the module
// This ensures Vite processes all images during build and applies correct hashes
const flagModules = import.meta.glob<string>(
  "../../../assets/icons/flags/*.svg",
  { eager: true, query: "?url", import: "default" }
);

// Create a map of country codes to their processed image URLs
const flagMap = Object.entries(flagModules).reduce(
  (acc, [path, url]) => {
    // Extract country code from path (e.g., "../../../assets/icons/flags/US.svg" -> "US")
    const countryCode = path.split("/").pop()?.replace(".svg", "") || "";
    acc[countryCode] = url;
    return acc;
  },
  {} as Record<string, string>
);

export type FlagIconProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /**
   * ISO 3166-1 alpha-2 country code (e.g., "US", "GB", "FR")
   */
  countryCode: string;
};

/**
 * FlagIcon component renders a country flag SVG based on ISO country code.
 * The flag SVG files are located in `assets/icons/flags/` and named with country codes.
 *
 * @example
 * ```tsx
 * <FlagIcon countryCode="US" width={24} height={16} />
 * <FlagIcon countryCode="GB" />
 * ```
 */
export const FlagIcon: FC<FlagIconProps> = ({
  countryCode,
  alt,
  width = 24,
  height = 16,
  ...props
}) => {
  if (!countryCode) {
    return null;
  }

  const code = countryCode.toUpperCase();
  const flagPath = flagMap[code];

  if (!flagPath) {
    return null;
  }

  return (
    <img
      src={flagPath}
      alt={alt || `${code} flag`}
      width={width}
      height={height}
      {...props}
    />
  );
};
